import { plural, t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, isArray, isDefined, KeyNames, noop, trimToken } from "@regrello/core-utils";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import type { ColDef, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import type { CustomFieldFrontendSelectableOption } from "../../../customFields/plugins/types/CustomFieldFrontendSelectableOption";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import { extractAllowedValuesFromFieldVersionForFrontend } from "../../../customFields/plugins/utils/extractAllowedValuesForFrontend";
import {
  normalizeSelectValue,
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloFormFieldSelect } from "../../../formFields/RegrelloFormFieldSelect";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { validateByConstraints } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellMultiSelect } from "./_internal/RegrelloEditableCellMultiSelect";
import type { MultiSelectPluginFrontendValue } from "./_internal/types";

type VALUE = MultiSelectPluginFrontendValue; // Saves some characters without using a troublesome generic type.

export class SpectrumMultiSelectFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.multiSelect";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.MULTI_SELECT;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.MULTI_SELECT,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public getConstraintConditionOperators() {
    return {
      ARRAY_CONTAINS_ONE_OF: {
        label: t`Contains any of`,
        inputCount: 1,
        isMultiselect: true,
      },
      ARRAY_EMPTY: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      ARRAY_NOT_EMPTY: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      ARRAY_EQUALS: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: true,
      },
      ARRAY_NOT_EQUALS: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: true,
      },
      ARRAY_CONTAINS_ALL: {
        label: t`Contains all of`,
        inputCount: 1,
        isMultiselect: true,
      },
      ARRAY_NOT_CONTAINS_ONE_OF: {
        label: t`Contains none of`,
        inputCount: 1,
        isMultiselect: true,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public renderPreviewFormField() {
    return (
      <RegrelloFormFieldSelect
        disabled={true}
        getOptionLabel={(option) => option}
        onChange={noop}
        options={[]}
        size="medium"
        value=""
      />
    );
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override parseTableValue(spectrumFieldVersion: SpectrumFieldVersionFields, valueIn: unknown) {
    // If the value is already an array of selected values, return it as-is
    if (Array.isArray(valueIn)) {
      return valueIn as VALUE;
    }

    if (valueIn == null || typeof valueIn !== "string") {
      return [];
    }

    // If it's a string (e.g., from pasting), look up the matching allowed value
    const stringValues = new Set(
      String(valueIn)
        .split(",")
        .map((value) => value.trim()),
    );
    const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(spectrumFieldVersion);
    const matchingValues = allowedValues.filter((value) => stringValues.has(value.displayValue));
    return matchingValues;
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellMultiSelect,
      cellEditorParams: {
        fieldInstance: options.fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      suppressKeyboardEvent: (params) => {
        // Submit on Cmd + Enter, but not on Enter, as that's the hotkey for selecting and
        // deselecting values in the multi-select input we're using.
        if (params.editing && params.event.key === KeyNames.ENTER && !params.event.metaKey) {
          return true;
        }
        return false;
      },
      valueFormatter: (value: ValueFormatterParams<TData, VALUE>): string => {
        if (value.value == null) {
          return "";
        }
        if (Array.isArray(value.value)) {
          return value.value.map((item) => item.displayValue).join(", ");
        }
        return String(value.value);
      },
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        if (params.value == null) {
          return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
        }
        if (Array.isArray(params.value)) {
          if (params.value.length === 0) {
            return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
          }
          return params.value.map((item) => item.displayValue).join(", ");
        }
        return String(params.value);
      },
      valueParser: (params: ValueParserParams<TData, VALUE>) => {
        return this.parseTableValue(options.fieldInstance.spectrumFieldVersion, params.newValue);
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: MultiSelectPluginFrontendValue,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      stringMultiValue: frontendValue.map((value) => value.value),
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const valueAsString = value.trim();

    const result = validateRequired(valueAsString, options.isRequired);
    if (result != null) {
      return result;
    }

    const allowedValueOptions = extractAllowedValuesFromFieldVersionForFrontend(options.fieldVersion);
    const allowedValuesByNormalizedValue = new Map(
      allowedValueOptions.map((option) => [normalizeSelectValue(option.value), option]),
    );

    const values = valueAsString.split(",").map(trimToken);
    const invalidValues = values.filter((token) => !allowedValuesByNormalizedValue.has(normalizeSelectValue(token)));

    if (invalidValues.length > 0) {
      const numValues = invalidValues.length;
      const commaSeparatedValues = invalidValues.join(", ");
      return {
        value: values.join(","),
        info: [
          {
            message: plural(numValues, {
              one: `Unrecognized value: ${commaSeparatedValues}`,
              other: `Unrecognized values: ${commaSeparatedValues}`,
            }),
            level: "error",
          },
        ],
      };
    }

    const selectedValues = values
      .map((multiSelectValue) => allowedValuesByNormalizedValue.get(normalizeSelectValue(multiSelectValue)))
      .filter(isDefined);

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, selectedValues);
    if (!spectrumValidationResult.isValid) {
      return {
        value: values.join(","),
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: selectedValues.map((option) => option.value).join(","),
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null || (isArray(value) && value.length === 0)) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (!isArray(value)) {
      console.error("Invalid value type for multi-select field plugin. This is a developer error.", value);
      return { isValid: false, errors: [t`Must be an array`] };
    }

    if (!this.isMultiSelectableOptionType(value)) {
      return { isValid: false, errors: [t`Must be an array of selectable options`] };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    const allowedValueOptions = extractAllowedValuesFromFieldVersionForFrontend(fieldVersion);

    const allowedValuesByNormalizedValue = new Map(allowedValueOptions.map((option) => [option.value, option]));
    const foundAllowedValues = value
      .map((selectedOption) => allowedValuesByNormalizedValue.get(selectedOption.value))
      .every(isDefined);
    if (!foundAllowedValues) {
      return { isValid: false, errors: [t`Must be a recognized value`] };
    }

    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }

  private isCustomSelectableOptionType(value: unknown): value is CustomFieldFrontendSelectableOption {
    return (
      value != null &&
      typeof value === "object" &&
      "id" in value &&
      typeof value.id === "number" &&
      "value" in value &&
      typeof value.value === "string" &&
      "displayValue" in value &&
      typeof value.displayValue === "string"
    );
  }

  private isMultiSelectableOptionType(value: unknown): value is VALUE {
    if (!isArray(value)) {
      return false;
    }
    return value.every((entry) => this.isCustomSelectableOptionType(entry));
  }
}
